import React from 'react';
import { ShieldCheck, TrendingUp, Sparkles, Clock, CheckCircle2 } from 'lucide-react';
import ProductInquiryCard from '../../components/ProductInquiryCard';

const PreIPO = () => {
  const advantages = [
    {
      title: "Direct CDSL / NSDL Demat Transfer",
      desc: "Shares are credited off-market directly to your personal Zerodha, Groww, AngelOne, or bank Demat account within 24 to 48 hours of transaction completion.",
      badge: "Instant Delivery",
      metric: "T+1 / T+2 Credit",
      icon: <CheckCircle2 className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "Entry at Institutional Valuations",
      desc: "Acquire shares months before official DRHP filing and retail IPO bidding to lock in attractive enterprise valuation multiples and potential listing gains.",
      badge: "Alpha Advantage",
      metric: "Pre-Listing Pricing",
      icon: <TrendingUp className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "Curated Balance Sheet Due Diligence",
      desc: "Each company is vetted for operating cash flows, cap table credibility, auditor reputations, and corporate governance before inclusion in our desk inventory.",
      badge: "Institutional Audit",
      metric: "Strict Vetting",
      icon: <ShieldCheck className="w-5 h-5 text-[#60A5FA]" />
    },
    {
      title: "SEBI Mandated 6-Month Lock-in Clarity",
      desc: "Clear regulatory navigation on the mandatory 6-month pre-IPO lock-in period applicable post-listing, with structured secondary liquidity options.",
      badge: "Compliance First",
      metric: "SEBI Aligned",
      icon: <Clock className="w-5 h-5 text-[#60A5FA]" />
    }
  ];

  return (
    <div className="min-h-screen bg-[#07131F] text-slate-100 selection:bg-[#16325B] selection:text-white pb-20">
      
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800/80 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(#3B82F6_1px,transparent_1px)] [background-size:24px_24px]"></div>
        
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          {/* <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16325B]/40 border border-blue-500/30 text-[#60A5FA] text-xs sm:text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 text-[#60A5FA] animate-pulse" />
            Direct Demat Credit • Late-Stage Unicorns & Market Leaders
          </div> */}

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-tight max-w-4xl mx-auto">
            Pre-IPO & <span className="text-[#60A5FA]">Unlisted Equities</span>
          </h1>

          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Participate in India's fastest-growing private enterprises, fintech disruptors, and profitable tech unicorns before they debut on NSE and BSE.
          </p>

          <div className="mt-8 flex justify-center gap-4">
            <a
              href="#inquire"
              className="px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#16325B] to-[#2563EB] hover:from-[#1D4ED8] hover:to-[#3B82F6] text-white font-bold transition-all duration-300 shadow-lg shadow-[#16325B]/40 hover:shadow-blue-500/30 hover:-translate-y-0.5 border border-blue-400/20"
            >
              Request Live Share Inventory
            </a>
          </div>
        </div>
      </section>

      {/* 2. STATS OVERVIEW BAR */}
      <section className="py-10 bg-[#091B2C] border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Starting Ticket Size</div>
            <div className="text-2xl font-bold text-[#60A5FA] font-mono mt-1">₹50,00,000+</div>
          </div>
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Delivery Mode</div>
            <div className="text-2xl font-bold text-white font-serif mt-1">Direct CDSL / NSDL</div>
          </div>
          <div className="p-4 bg-[#0B1E30]/70 rounded-2xl border border-slate-700/60 shadow-lg">
            <div className="text-xs uppercase text-slate-400 font-semibold tracking-wider">Average Horizon</div>
            <div className="text-2xl font-bold text-blue-300 font-serif mt-1">12 – 36 Months</div>
          </div>
        </div>
      </section>

      {/* 3. CORE ADVANTAGES & INQUIRY FORM */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Left Column: Strategic Advantages */}
          <div className="lg:col-span-7 space-y-5">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-6">
              Institutional Unlisted Advantages
            </h2>

            <div className="space-y-4">
              {advantages.map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-[#0B1E30]/80 backdrop-blur-sm border border-slate-700/60 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#16325B]/40 border border-blue-500/30 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white tracking-tight">{item.title}</h3>
                    </div>
                    <span className="text-[11px] font-mono font-semibold px-2.5 py-1 rounded-full bg-[#16325B]/40 text-[#60A5FA] border border-blue-500/25">
                      {item.metric}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed pl-13">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Regulatory Notice */}
            <div className="mt-8 p-5 rounded-2xl bg-[#081523] border border-slate-800 text-xs text-slate-400 leading-relaxed">
              <div className="flex items-center gap-2 text-[#60A5FA] font-semibold mb-1">
                <ShieldCheck className="w-4 h-4 text-[#60A5FA]" />
                <span>Statutory Compliance Notice</span>
              </div>
              All transactions are conducted strictly through verified off-market escrow mechanisms and valid Delivery Instruction Slips (DIS/e-DIS). Unlisted equity investments are subject to market risks, illiquidity until listing, and company-specific fundamental changes.
            </div>
          </div>

          {/* Right Column: Premium Animated Form */}
          <div id="inquire" className="lg:col-span-5 sticky top-24">
            <ProductInquiryCard
              title="Request Pre-IPO Price List"
              subtitle="Get the latest lot availability, valuation reports, and unlisted stock prices delivered securely."
              productName="Pre-IPO & Unlisted Equities"
              defaultTicket="₹1 Lakh to ₹5 Lakhs"
              buttonText="Get Live Unlisted Rates"
            />
          </div>

        </div>
      </section>

    </div>
  );
};

export default PreIPO;